/**
 * What each bus is turned up to, and the curve between a slider and a gain.
 *
 * Module level rather than owned by an engine, and that is the whole point. The audio context does
 * not exist until the player taps to play, a settings screen can be opened from the main menu long
 * before any mode has started, and a profile applies its settings on boot. A level set at any of
 * those moments has to survive until there is something to apply it to, so the value lives here and
 * the bus reads it whenever it happens to be built. The same shape `ui/hud/hudScale.ts` uses for
 * the HUD, and for the same reason: a knob names a quantity and hands the value to whoever owns it.
 *
 * Nothing here imports Tone, or knows a gain node exists. It is arithmetic and a subscriber list,
 * which is what makes it the one part of the mix that can be unit tested.
 */

/** The four buses. `effects`, `music` and `ui` feed `master`, and `master` feeds the speakers. */
export type MixBusId = 'master' | 'effects' | 'music' | 'ui';

/** Walked wherever every bus has to be visited, so the order is stated once. */
export const MIX_BUS_ORDER: readonly MixBusId[] = ['master', 'effects', 'music', 'ui'];

/** What a bus feeds. `null` is the one that feeds the destination. */
export const MIX_BUS_PARENT: Readonly<Record<MixBusId, MixBusId | null>> = {
  master: null,
  effects: 'master',
  music: 'master',
  ui: 'master',
};

export const MIN_MIX_LEVEL = 0;
export const MAX_MIX_LEVEL = 1;

/**
 * Where each bus starts.
 *
 * Not all 1. The range is loud and the master used to be pinned at 0.5, so master opens near where
 * the game has always sounded and leaves headroom above it rather than making every existing
 * session louder than it was. Music sits under the rest because a bed nobody asked for should be
 * quieter than the gunfire it is under.
 */
export const DEFAULT_MIX_LEVELS: Readonly<Record<MixBusId, number>> = {
  master: 0.7,
  effects: 1,
  music: 0.6,
  ui: 0.8,
};

/**
 * How much gain is left at half a slider.
 *
 * Ten decibels down. Perceived loudness roughly halves per ten decibels, which is the whole claim
 * the curve below is built on: a slider at half should sound half as loud, not measure half the
 * amplitude. A linear slider spends its top third on changes nobody can hear and its bottom third
 * dropping off a cliff.
 */
const HALF_SLIDER_GAIN = 0.316;

/** The exponent that puts `HALF_SLIDER_GAIN` at exactly half a slider. Around 1.66. */
const LOUDNESS_EXPONENT = Math.log(HALF_SLIDER_GAIN) / Math.log(0.5);

const levels: Record<MixBusId, number> = { ...DEFAULT_MIX_LEVELS };

const listeners = new Set<() => void>();

/** What the player set this bus to, 0 to 1. The number that goes to disk. */
export function mixLevel(bus: MixBusId): number {
  return levels[bus];
}

/**
 * Sets a bus, clamped, and tells whoever is listening.
 *
 * Called on every pixel of a drag, so it does nothing when the value has not actually moved: a
 * slider held still at the end of its travel should not ramp a gain node sixty times a second.
 */
export function setMixLevel(bus: MixBusId, level: number): void {
  const next = clampLevel(level);
  if (next === levels[bus]) return;

  levels[bus] = next;
  for (const listener of listeners) listener();
}

/** Every bus back to the value it shipped with. What a fresh profile is. */
export function resetMixLevels(): void {
  for (const bus of MIX_BUS_ORDER) setMixLevel(bus, DEFAULT_MIX_LEVELS[bus]);
}

/**
 * Amplitude for a slider position.
 *
 * Zero is exactly zero, and that is a rule rather than a consequence of the curve. Any exponent of
 * a very small number is a very small number, and a mix that bottoms out at a thousandth of full
 * scale is a session the player asked to silence and can still faintly hear.
 */
export function busGain(level: number): number {
  const clamped = clampLevel(level);
  if (clamped <= MIN_MIX_LEVEL) return 0;
  return clamped ** LOUDNESS_EXPONENT;
}

/** What this bus is worth right now, as a gain. What a bus node is set to. */
export function busGainNow(bus: MixBusId): number {
  return busGain(mixLevel(bus));
}

/**
 * Runs `listener` whenever any level moves, and stops when the returned function is called.
 *
 * A live mix bus subscribes, which is what makes a drag audible while the thumb is still down. A
 * bus built after the fact needs no notification: it reads the current value on the way up.
 */
export function watchMixLevels(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return MIN_MIX_LEVEL;
  return Math.min(MAX_MIX_LEVEL, Math.max(MIN_MIX_LEVEL, level));
}
